import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getOrdersByUser } from "@/services/supabaseService";
import type { Order } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type OrderFilter = "all" | "ordered" | "listed" | "completed";

const STATUS_MAP: Record<string, string> = {
  "待付款": "待付款",
  "待發貨": "已訂貨",
  "已發貨": "已上架",
  "已完成": "銷售完畢",
  "已取消": "已取消",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("zh-TW", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).replace(/\//g, "-");
}

export default function MyOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState<OrderFilter>("all");
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    setLoadError("");
    getOrdersByUser(user.id)
      .then((o) => setOrders(o))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[MyOrders] 載入訂單失敗:", err);
        if (msg.includes("row-level security") || msg.includes("RLS")) {
          setLoadError("無法讀取訂單：權限不足，請聯繫管理員");
        } else if (msg.includes("timeout") || msg.includes("network")) {
          setLoadError("網路連線超時，請檢查網路後重試");
        } else {
          setLoadError("載入訂單失敗：" + msg);
        }
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-lock-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">請先登入分銷商帳號</p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 cursor-pointer"
          >
            前往登入
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const filteredOrders = orders.filter((o) => {
    if (filter === "ordered") return o.status === "待發貨";
    if (filter === "listed") return o.status === "已發貨";
    if (filter === "completed") return o.status === "已完成";
    return true;
  });

  const stats = {
    all: orders.length,
    ordered: orders.filter((o) => o.status === "待發貨").length,
    listed: orders.filter((o) => o.status === "已發貨").length,
    completed: orders.filter((o) => o.status === "已完成").length,
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "待發貨": return "bg-amber-100 text-amber-700";
      case "已發貨": return "bg-blue-100 text-blue-700";
      case "已完成": return "bg-emerald-100 text-emerald-700";
      case "已取消": return "bg-gray-100 text-gray-500";
      default: return "bg-gray-100 text-gray-500";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "待發貨": return "ri-time-line";
      case "已發貨": return "ri-truck-line";
      case "已完成": return "ri-check-double-line";
      case "已取消": return "ri-close-line";
      default: return "ri-question-line";
    }
  };

  const emptyText =
    filter === "all"
      ? "還沒有訂單紀錄，快去選品下單吧"
      : filter === "ordered"
      ? "沒有已訂貨的訂單"
      : filter === "listed"
      ? "沒有已上架的訂單"
      : "沒有銷售完畢的訂單";

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12 max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">
              個人中心
            </button>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-700">我的訂單</span>
          </div>

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">我的訂單</h1>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-700 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs"></i>
              </div>
              返回個人中心
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { key: "all" as const, label: "全部訂單", count: stats.all },
              { key: "ordered" as const, label: "已訂貨", count: stats.ordered },
              { key: "listed" as const, label: "已上架", count: stats.listed },
              { key: "completed" as const, label: "銷售完畢", count: stats.completed },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`text-center py-3 rounded-xl border transition-all cursor-pointer ${
                  filter === s.key
                    ? "bg-white border-amber-400 shadow-sm"
                    : "bg-white border-gray-100 hover:border-gray-200"
                }`}
              >
                <p className="text-lg font-bold text-gray-900">{s.count}</p>
                <p className={`text-xs mt-0.5 ${filter === s.key ? "text-amber-600" : "text-gray-500"}`}>
                  {s.label}
                </p>
              </button>
            ))}
          </div>

          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 inline-flex">
            {[
              { key: "all" as const, label: "全部" },
              { key: "ordered" as const, label: "已訂貨" },
              { key: "listed" as const, label: "已上架" },
              { key: "completed" as const, label: "銷售完畢" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filter === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {loadError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 flex items-center justify-between">
                <span>{loadError}</span>
                <button
                  onClick={() => window.location.reload()}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-100 text-red-700 font-medium hover:bg-red-200 cursor-pointer"
                >
                  重新載入
                </button>
              </div>
            )}
            {isLoading ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">載入中...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-4xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 text-lg mb-2">暫無訂單</p>
                <p className="text-gray-400 text-sm mb-4">{emptyText}</p>
                <button
                  onClick={() => navigate("/products")}
                  className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 cursor-pointer"
                >
                  去選品
                </button>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-900">{order.id}</span>
                      <span className="text-xs text-gray-400">{fmtDate(order.created_at)}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${getStatusStyle(order.status)}`}>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${getStatusIcon(order.status)} text-[10px]`}></i>
                      </div>
                      {STATUS_MAP[order.status] || order.status}
                    </span>
                  </div>
                  <div className="px-5 py-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">合計</span>
                      <span className="text-lg font-bold text-gray-900">${Number(order.total_amount).toLocaleString()}</span>
                    </div>
                    {order.tracking_number && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500">物流單號：</span>
                        <span className="text-xs font-medium text-gray-700">{order.tracking_number}</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(order.tracking_number || "")}
                          className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer"
                          title="複製單號"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-file-copy-line text-xs"></i>
                          </div>
                        </button>
                      </div>
                    )}
                    {order.shipping_address && (
                      <p className="text-xs text-gray-400 mt-1">{order.shipping_address}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}